"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import SearchBar from "../../../../components/SearchBar";
import FormInput from "../../../../components/FormInput";
import Dropdown from "../../../../components/Dropdown";
import Pagination from "../../../../components/Pagination";
import { listCompletedOrders as apiListCompletedOrders } from "../../../../api/frontShop/CompletedOrdersAPI";
import { listRefundedOrders as apiListRefundedOrders } from "../../../../api/frontShop/RefundsAPI";
import { getPaymentOptions as apiGetPaymentOptions } from "../../../../api/frontShop/CheckOutAPI";
import styles from "../css/completedOrders.module.css";

const PAGE_SIZE = 8;

const toReceiptLabel = (recieptno, orderid) => {
  if (recieptno && String(recieptno).trim()) return String(recieptno).trim();
  if (orderid != null) return String(orderid);
  return "";
};

const normalizeReceiptSearch = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^rec-/, "");

export default function CompletedOrdersView() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [refundedOrderIds, setRefundedOrderIds] = useState(() => new Set());
  const [refundedReceiptNos, setRefundedReceiptNos] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [paymentType, setPaymentType] = useState("All Payment Types");
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadError("");
      try {
        const [{ data: json }, { data: refundedJson }, { data: optJson }] = await Promise.all([
          apiListCompletedOrders(),
          apiListRefundedOrders(),
          apiGetPaymentOptions(),
        ]);
        const rows = Array.isArray(json?.data) ? json.data : [];
        const mapped = rows.map((r) => {
          const completedAt = r?.completedAt ? new Date(r.completedAt) : new Date();
          const method = r?.paymentMethod || "Card";
          const amount = Number(r?.totalamount || 0);
          return {
            id: toReceiptLabel(r?.recieptno, r?.orderid),
            receiptno: r?.recieptno || null,
            orderid: r?.orderid,
            completedAt,
            method,
            amount: Number.isFinite(amount) ? amount : 0,
          };
        });
        const orderids = Array.isArray(refundedJson?.data?.orderids) ? refundedJson.data.orderids : [];
        const recieptnos = Array.isArray(refundedJson?.data?.recieptnos) ? refundedJson.data.recieptnos : [];
        const orderidSet = new Set(orderids.map((n) => Number(n)).filter((n) => Number.isFinite(n)));
        const recieptnoSet = new Set(recieptnos.map((s) => String(s || "").trim()).filter((s) => s));

        const optRows = Array.isArray(optJson?.data) ? optJson.data : [];
        const opts = optRows
          .map((r) => ({
            optionid: r?.optionid != null ? String(r.optionid).trim() : "",
            paymentname: r?.paymentname != null ? String(r.paymentname).trim() : "",
          }))
          .filter((r) => r.optionid && r.paymentname);

        if (!cancelled) {
          setOrders(mapped);
          setRefundedOrderIds(orderidSet);
          setRefundedReceiptNos(recieptnoSet);
          setPaymentOptions(opts);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || "Failed to load completed orders");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const paymentTypeOptions = useMemo(() => {
    const names = paymentOptions.map((o) => o.paymentname);
    const unique = Array.from(new Set(names));
    const base = ["All Payment Types", ...unique, "Refunds"];
    return base;
  }, [paymentOptions]);

  const isRefundedOrder = useMemo(() => {
    return (order) =>
      refundedOrderIds.has(Number(order?.orderid)) ||
      (order?.receiptno && refundedReceiptNos.has(String(order.receiptno).trim())) ||
      refundedReceiptNos.has(String(order?.id || "").trim());
  }, [refundedOrderIds, refundedReceiptNos]);

  const filtered = useMemo(() => {
    const q = normalizeReceiptSearch(search);
    const result = orders.filter((order) => {
      const matchesSearch =
        !q ||
        normalizeReceiptSearch(order.id).includes(q) ||
        (order.receiptno && normalizeReceiptSearch(order.receiptno).includes(q));
      const matchesMethod =
        paymentType === "All Payment Types" ||
        (paymentType === "Refunds" ? isRefundedOrder(order) : order.method === paymentType);
      const matchesDate = !selectedDate ||
        (order.completedAt.getFullYear() === selectedDate.getFullYear() &&
         order.completedAt.getMonth() === selectedDate.getMonth() &&
         order.completedAt.getDate() === selectedDate.getDate());
      return matchesSearch && matchesMethod && matchesDate;
    });
    // Sort by date descending (latest first)
    return result.sort((a, b) => b.completedAt - a.completedAt);
  }, [orders, search, paymentType, selectedDate, isRefundedOrder]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [search, paymentType, selectedDate]);

  const handleRowClick = (order) => {
    const m = String(order.method || "").trim().toLowerCase();
    if (m === "cash") {
      navigate("/frontshop/orders/cash", { state: { recieptno: order.receiptno || order.id } });
    } else if (m === "card" || m === "nedbank" || m === "ikhokha") {
      navigate("/frontshop/orders/card", { state: { paymentMethod: order.method, recieptno: order.receiptno || order.id } });
    } else if (m === "cod") {
      navigate("/frontshop/orders/cod", { state: { recieptno: order.receiptno || order.id } });
    } else if (m === "credit") {
      navigate("/frontshop/orders/credit", { state: { recieptno: order.receiptno || order.id } });
    } else if (m === "split payments" || m === "split") {
      navigate("/frontshop/orders/split", { state: { recieptno: order.receiptno || order.id } });
    } else if (m === "medical aid" || m === "medical-aid") {
      navigate("/frontshop/orders/medical-aid", { state: { recieptno: order.receiptno || order.id } });
    }
  };

  const formatDateLine = (d, method) => {
    const options = { month: "short", day: "numeric", year: "numeric" };
    const datePart = d.toLocaleDateString(undefined, options);
    const timePart = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return `${datePart} at ${timePart} ${method}`;
  };

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={() => navigate("/frontshop")} />
      <main className={styles.main}>
        <div className={styles.card}>
          <header className={styles.header}> 
            <div>
              <h1 className={styles.title}>Completed Transactions</h1>
              <p className={styles.subtitle}>{filtered.length} orders found</p>
            </div>
          </header>

          <div className={styles.filtersRow}>
            <div className={styles.searchColumn}>
              <SearchBar value={search} onChange={setSearch} placeholder="Search orders..." />
            </div>
           <div className={styles.controlsColumn}>
  <div className={styles.dateControl}>
    <FormInput
      type="date"
      name="orderDateFilter"
      value={selectedDate ? selectedDate.toISOString().slice(0, 10) : ""}
      onChange={(e) =>
        setSelectedDate(
          e.target.value ? new Date(`${e.target.value}T00:00:00`) : null
        )
      }
    />
    {selectedDate && (
      <button
        type="button"
        onClick={() => setSelectedDate(null)}
        className={styles.clearDateBtn}
        aria-label="Clear date"
      >
        ×
      </button>
    )}
  </div>
  <Dropdown
    label=""
    name="paymentType"
    options={paymentTypeOptions}
    value={paymentType}
    onChange={setPaymentType}
    placeholder="Filter by payment type"
  />
</div>
          </div>

          <div className={styles.list}> 
            {loadError && (
              <div className={styles.emptyState}>{loadError}</div>
            )}
            {!loadError && loading && (
              <div className={styles.emptyState}>Loading...</div>
            )}
            {!loadError && !loading && pageData.length === 0 && (
              <div className={styles.emptyState}>No completed orders found</div>
            )}
            {pageData.map((order) => (
              <div
                key={order.id}
                className={styles.row}
                onClick={() => handleRowClick(order)}
              > 
                <div className={styles.rowMain}> 
                  <div className={styles.orderId}>{order.id}</div>
                  <div className={styles.meta}>{formatDateLine(order.completedAt, order.method)}</div>
                </div>
                <div className={styles.rowRight}> 
                  <div className={styles.badges}> 
                    <span className={`${styles.badge} ${styles.badgePaid}`}>Paid</span>
                    {isRefundedOrder(order) ? (
                      <span className={`${styles.badge} ${styles.badgeRefunded}`}>Refunded</span>
                    ) : (
                      <span className={`${styles.badge} ${styles.badgeFulfilled}`}>Fulfilled</span>
                    )}
                  </div>
                  <div className={styles.amount}>R {order.amount.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.paginationWrap}>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      </main>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
